import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Wallet, Landmark, TrendingUp } from 'lucide-react';
import { tresorerieApi } from '../../services/tresorerieApi';
import { formatCurrency } from '../../utils/formatters';

const num = (s: string | number | null | undefined) => Number(s ?? 0);

const PositionTresorerie: React.FC = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['tresorerie', 'position'],
    queryFn: () => tresorerieApi.getPosition(),
  });

  if (isLoading || !data) {
    return <div className="p-6 text-gray-500">Chargement de la position de trésorerie…</div>;
  }

  return (
    <div className="w-full space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-2xl p-5 bg-white shadow-sm flex items-center gap-3">
          <span className="p-2.5 rounded-xl bg-green-100 text-green-600"><Wallet className="h-5 w-5" /></span>
          <div>
            <p className="text-sm text-gray-500">Solde caisse (espèces)</p>
            <p className="text-xl font-bold text-gray-900">{formatCurrency(num(data.solde_caisse))}</p>
          </div>
        </div>
        <div className="rounded-2xl p-5 bg-white shadow-sm flex items-center gap-3">
          <span className="p-2.5 rounded-xl bg-blue-100 text-blue-600"><Landmark className="h-5 w-5" /></span>
          <div>
            <p className="text-sm text-gray-500">Total comptes bancaires</p>
            <p className="text-xl font-bold text-gray-900">{formatCurrency(num(data.total_banques))}</p>
          </div>
        </div>
        <div className="relative overflow-hidden rounded-2xl p-5 bg-gradient-to-br from-indigo-600 to-blue-600 text-white shadow-sm">
          <TrendingUp className="h-6 w-6 mb-2 opacity-80" />
          <p className="text-sm opacity-80">Trésorerie totale</p>
          <p className="text-2xl font-bold">{formatCurrency(num(data.tresorerie_totale))}</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">Détail par compte bancaire</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100">
            <thead className="bg-gray-50">
              <tr className="text-left text-xs font-medium text-gray-500 uppercase">
                <th className="px-4 py-2">Compte</th>
                <th className="px-4 py-2">Banque</th>
                <th className="px-4 py-2">Type</th>
                <th className="px-4 py-2 text-right">Solde courant</th>
                <th className="px-4 py-2 text-right">Solde rapproché</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {data.comptes.map((c) => (
                <tr key={c.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-sm text-gray-800">{c.nom}</td>
                  <td className="px-4 py-2 text-sm text-gray-500">{c.banque || '—'}</td>
                  <td className="px-4 py-2 text-sm text-gray-600">{c.type_compte}</td>
                  <td className="px-4 py-2 text-sm text-right font-semibold text-gray-900">{formatCurrency(num(c.solde_courant))}</td>
                  <td className="px-4 py-2 text-sm text-right text-gray-600">{formatCurrency(num(c.solde_rapproche))}</td>
                </tr>
              ))}
              {data.comptes.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">Aucun compte bancaire enregistré.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PositionTresorerie;
